import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MapPin,
  CheckCircle2,
  Phone,
  Mail,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Star,
  Users,
  Award,
  BookOpen,
  ArrowRight,
  TrendingUp,
  Building2,
  Calendar,
  Layers,
  Sparkles,
  Play,
  Volume2,
  VolumeX,
  X,
  Menu,
  Clock,
  Laptop,
  Calculator
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/layout/Footer";
import {
  CITIES_DATA,
  CITY_COURSES,
  MENTOR_INFO,
  CITY_FAQS,
  CITY_FAQS_BY_SLUG,
  CITY_PATHS,
  VIDEO_TESTIMONIALS
} from "@/data/cityData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const API = `${BACKEND_URL}/api`;

const PATH_TO_CITY_SLUG = {
  "/stock-market-course-in-jaipur": "jaipur",
  "/stock-market-course-in-jaipur/": "jaipur",
  "/stock-market-classes-in-jodhpur": "jodhpur",
  "/stock-market-classes-in-jodhpur/": "jodhpur",
  "/stock-market-classes-in-udaipur": "udaipur",
  "/stock-market-classes-in-udaipur/": "udaipur",
  "/stock-market-classes-in-kota": "kota",
  "/stock-market-classes-in-kota/": "kota",
  "/stock-market-classes-in-ajmer": "ajmer",
  "/stock-market-classes-in-ajmer/": "ajmer",
  "/stock-market-classes-in-sikar": "sikar",
  "/stock-market-classes-in-sikar/": "sikar",
  "/stock-market-classes-in-bhilwara": "bhilwara",
  "/stock-market-classes-in-bhilwara/": "bhilwara",
};

export default function CityLandingPage() {
  const { citySlug } = useParams();
  const location = useLocation();

  const activeSlug = useMemo(() => {
    const rawPath = location.pathname.toLowerCase().replace(/\/+$/, "") || "/";
    const withSlash = rawPath + "/";
    const mapped = PATH_TO_CITY_SLUG[rawPath] || PATH_TO_CITY_SLUG[withSlash];
    if (mapped) return mapped;
    if (citySlug && CITIES_DATA[citySlug.toLowerCase()]) {
      return citySlug.toLowerCase();
    }
    return "jaipur";
  }, [citySlug, location.pathname]);

  const city = CITIES_DATA[activeSlug] || CITIES_DATA.jaipur;
  const currentCityFaqs = CITY_FAQS_BY_SLUG[activeSlug] || CITY_FAQS_BY_SLUG.jaipur || CITY_FAQS;

  // Selected Course for Full Syllabus Modal
  const [modalCourse, setModalCourse] = useState(null);

  // Video review playing state
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [isMuted, setIsMuted] = useState(true);

  // FAQ accordion state
  const [openFaq, setOpenFaq] = useState(0);

  // Mobile menu toggle
  const [mobileMenu, setMobileMenu] = useState(false);

  // Interactive Capital Calculator state
  const [capital, setCapital] = useState(50000);
  const [riskPercent, setRiskPercent] = useState(1.5);
  const [rrRatio, setRrRatio] = useState(2.0);

  const riskAmount = useMemo(() => Math.round((capital * riskPercent) / 100), [capital, riskPercent]);
  const rewardAmount = useMemo(() => Math.round(riskAmount * rrRatio), [riskAmount, rrRatio]);
  const simulatedNet = useMemo(() => {
    const wins = 11 * rewardAmount; // 55% win rate on 20 trades
    const losses = 9 * riskAmount;
    return wins - losses;
  }, [riskAmount, rewardAmount]);

  // Enrollment Form state
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    course: "Super Trader Combo (Mentorship)",
    learningMode: "Offline Live Classroom",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Ensure the page always starts at top on mount and city change
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } else {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeSlug, location.pathname, location.hash]);

  const handleEnrollClick = (courseTitle) => {
    setFormData((prev) => ({ ...prev, course: courseTitle }));
    if (modalCourse) setModalCourse(null);
    const enrollEl = document.getElementById("enroll");
    if (enrollEl) {
      enrollEl.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || `${formData.phone}@lead.jamadhan.com`,
        message: `[City: ${city.name}] [Mode: ${formData.learningMode}] [Course: ${formData.course}] ${formData.message || "Enquiry from " + city.name + " city landing page"}`,
        source: `city-landing-${city.slug}`
      };

      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      toast.success(`Thank you, ${formData.name}! Our admissions mentor will contact you shortly.`);
    } catch (err) {
      console.error("Lead submission error:", err);
      setSubmitted(true);
      toast.success("Thank you! Your enrollment inquiry has been registered.");
    } finally {
      setSubmitting(false);
    }
  };

  // Structured Schema for Local SEO
  const canonicalUrl = `https://jamadhan.com${CITY_PATHS[city.slug] || `/stock-market-classes-in-${city.slug}/`}`;

  const structuredData = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "EducationalOrganization",
          "@id": `${canonicalUrl}#organization`,
          "name": `JamaDhan Stock Market Institute - ${city.name}`,
          "image": "https://jamadhan.com/images/cities/city_academy_hero.jpg",
          "url": canonicalUrl,
          "telephone": city.phone,
          "priceRange": "₹0 - ₹24999",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": city.address,
            "addressLocality": city.name,
            "addressRegion": "Rajasthan",
            "addressCountry": "IN"
          }
        },
        {
          "@type": "Course",
          "name": `Stock Market Course in ${city.name}`,
          "description": city.metaDescription,
          "provider": {
            "@type": "Organization",
            "name": "JamaDhan Academy",
            "sameAs": "https://jamadhan.com"
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": currentCityFaqs.map((faq) => ({
            "@type": "Question",
            "name": ((faq.question || faq.q || "")).replace("{cityName}", city.name),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": ((faq.answer || faq.a || "")).replace("{cityName}", city.name)
            }
          }))
        }
      ]
    };
  }, [city, canonicalUrl, currentCityFaqs]);

  // If visited via legacy /city/:citySlug route, cleanly redirect to the canonical localized URL
  if (citySlug && CITY_PATHS[citySlug.toLowerCase()]) {
    return <Navigate to={CITY_PATHS[citySlug.toLowerCase()]} replace />;
  }

  return (
    <div className="min-h-screen bg-[#02050A] text-white selection:bg-[#00FF88]/20 selection:text-[#00FF88] font-sans antialiased">
      {/* ═══ SEO HELMET ═══ */}
      <Helmet>
        <title>{city.metaTitle}</title>
        <meta name="description" content={city.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={city.metaTitle} />
        <meta property="og:description" content={city.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* ═══ TOP CITY ANNOUNCEMENT BANNER ═══ */}
      <div className="relative z-20 bg-gradient-to-r from-[#00FF88]/15 via-[#00F0FF]/15 to-[#00FF88]/15 border-b border-[#00FF88]/25 py-2 px-4 text-center">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-xs font-mono text-zinc-300">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#00FF88]/20 text-[#00FF88] text-[11px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-ping" />
            LIVE ADMISSIONS
          </span>
          <span className="hidden sm:inline text-zinc-300">
            Admissions Open for {city.name} Batch: Live Market Offline Floor & Online Hybrid Cohorts
          </span>
          <a href="#enroll" className="text-[#00FF88] font-bold underline hover:text-white transition-colors ml-2">
            Reserve Seat →
          </a>
        </div>
      </div>

      {/* ═══ ORIGINAL MAIN SITE HEADER / NAVBAR ═══ */}
      <nav className="sticky top-0 z-50 w-full backdrop-blur-xl bg-[#050505]/90 border-b border-white/5 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.3)]">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-lg font-bold font-outfit tracking-tight text-white">JAMADHAN</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-[#CBD5E1] hover:text-[#00FF88] transition-colors font-medium tracking-wide">Home</Link>
            <Link to="/about/" className="text-sm text-[#CBD5E1] hover:text-[#00FF88] transition-colors font-medium tracking-wide">About</Link>
            <a href="/#courses" className="text-sm text-[#CBD5E1] hover:text-[#00FF88] transition-colors font-medium tracking-wide">Courses</a>
            <Link to="/gallery/" className="text-sm text-[#CBD5E1] hover:text-[#00FF88] transition-colors font-medium tracking-wide">Gallery</Link>
            <Link to="/contact/" className="text-sm text-[#CBD5E1] hover:text-[#00FF88] transition-colors font-medium tracking-wide">Contact</Link>
            <Link to="/drishti-ai/" className="text-sm text-[#00F0FF] hover:text-[#00FF88] font-semibold transition-colors">Drishti AI</Link>

            <Link
              to="/login"
              className="bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white hover:shadow-[0_0_20px_rgba(0,255,136,0.3)] border border-transparent hover:border-[#00FF88] rounded-sm px-5 py-1.5 text-sm transition-all duration-200"
            >
              Login
            </Link>
          </div>

          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden text-white" aria-label="Toggle mobile menu">
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenu && (
          <div className="md:hidden bg-[#050505]/98 border-t border-white/5 text-white backdrop-blur-xl px-6 pb-4">
            <Link to="/" className="block py-3 text-[#CBD5E1] hover:text-[#00FF88] border-b border-white/5">Home</Link>
            <Link to="/about/" className="block py-3 text-[#CBD5E1] hover:text-[#00FF88] border-b border-white/5">About</Link>
            <a href="/#courses" className="block py-3 text-[#CBD5E1] hover:text-[#00FF88] border-b border-white/5">Courses</a>
            <Link to="/gallery/" className="block py-3 text-[#CBD5E1] hover:text-[#00FF88] border-b border-white/5">Gallery</Link>
            <Link to="/contact/" className="block py-3 text-[#CBD5E1] hover:text-[#00FF88] border-b border-white/5">Contact</Link>
            <Link to="/drishti-ai/" className="block py-3 text-[#00F0FF] hover:text-[#00FF88] border-b border-white/5 font-semibold">Drishti AI</Link>
            <Link to="/login" className="block mt-3 text-center py-2 bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white rounded-sm">Login</Link>
          </div>
        )}
      </nav>

      {/* ═══ HERO SECTION ═══ */}
      <section className="relative overflow-hidden pt-12 pb-16 px-4 sm:px-6 lg:px-8 border-b border-white/[0.06]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-6xl h-96 bg-gradient-to-b from-[#00FF88]/10 via-[#00F0FF]/5 to-transparent blur-3xl pointer-events-none -z-10" />

        <div className="max-w-6xl mx-auto text-center space-y-6">
          {/* Location Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.1] text-xs font-mono text-zinc-300 shadow-inner">
            <MapPin className="w-3.5 h-3.5 text-[#00FF88]" />
            <span>{city.heroBadge || `${city.name} Trading Hub`}</span>
            {city.centerType && (
              <>
                <span className="text-zinc-600">•</span>
                <span className="text-[#00FF88] font-semibold">{city.centerType}</span>
              </>
            )}
          </div>

          {/* Main H1 Headline */}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black font-outfit text-white tracking-tight leading-[1.1]">
            Stock Market Institute in{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] via-[#00F0FF] to-[#00FF88]">
              {city.name}
            </span>
            <br />
            <span className="text-2xl sm:text-3xl lg:text-4xl text-zinc-300 font-semibold mt-2 block">
              {city.heroHeading || "Learn Institutional Price Action & Derivatives Under Deepak Sethia"}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="max-w-3xl mx-auto text-sm sm:text-base text-zinc-400 leading-relaxed">
            {city.heroSubheading || city.heroDescription || city.metaDescription}
          </p>

          {/* Quick Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-xs text-zinc-300">
              <ShieldCheck className="w-4 h-4 text-[#00FF88]" />
              <span>NISM Aligned Curriculum</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-xs text-zinc-300">
              <TrendingUp className="w-4 h-4 text-[#00F0FF]" />
              <span>Live Market Floor Execution</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-xs text-zinc-300">
              <Users className="w-4 h-4 text-[#00FF88]" />
              <span>10,000+ Alumni Community</span>
            </div>
          </div>

          {/* Primary Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <a
              href="#courses"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-[#00FF88] to-[#00F0FF] text-black font-bold text-sm tracking-wide shadow-lg shadow-[#00FF88]/20 hover:opacity-95 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <BookOpen className="w-4 h-4 text-black" />
              <span>Explore 4 Courses & Syllabus</span>
              <ArrowRight className="w-4 h-4 text-black" />
            </a>

            <a
              href="#enroll"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white/[0.05] border border-white/[0.15] text-white font-semibold text-sm hover:bg-white/[0.08] hover:border-white/[0.25] transition-all flex items-center justify-center gap-2"
            >
              <Calendar className="w-4 h-4 text-[#00FF88]" />
              <span>Book Free Demo Session</span>
            </a>
          </div>

          {/* Hero Metrics Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8 max-w-4xl mx-auto border-t border-white/[0.06]">
            <div className="p-4 rounded-2xl bg-[#060D1A]/80 border border-white/[0.06] text-center">
              <div className="text-2xl sm:text-3xl font-black font-outfit text-white">10,000+</div>
              <div className="text-xs text-zinc-400 mt-0.5">Students Trained Across India</div>
            </div>
            <div className="p-4 rounded-2xl bg-[#060D1A]/80 border border-white/[0.06] text-center">
              <div className="text-2xl sm:text-3xl font-black font-outfit text-[#00FF88]">4.9 / 5.0</div>
              <div className="text-xs text-zinc-400 mt-0.5">Google Student Rating</div>
            </div>
            <div className="p-4 rounded-2xl bg-[#060D1A]/80 border border-white/[0.06] text-center">
              <div className="text-2xl sm:text-3xl font-black font-outfit text-[#00F0FF]">12+ Yrs</div>
              <div className="text-xs text-zinc-400 mt-0.5">Institutional Trading Track Record</div>
            </div>
            <div className="p-4 rounded-2xl bg-[#060D1A]/80 border border-white/[0.06] text-center">
              <div className="text-2xl sm:text-3xl font-black font-outfit text-white">100%</div>
              <div className="text-xs text-zinc-400 mt-0.5">Practical Live Market Trading</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION: THE 4 CORE COURSES (FULL DETAILED VIEW & SYLLABUS EXPANSION) ═══ */}
      <section id="courses" className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 border-b border-white/[0.06] bg-[#030712]">
        <div className="max-w-7xl mx-auto space-y-12">
          
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/30 text-xs font-mono text-[#00FF88]">
              <Sparkles className="w-3.5 h-3.5" />
              <span>4 Structured Learning Programs</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black font-outfit text-white tracking-tight">
              Our 4 Stock Market Courses for {city.name}
            </h2>
            <p className="text-sm text-zinc-400">
              From absolute beginners to institutional option sellers. Click <strong className="text-white">"View Full Syllabus"</strong> on any course to open its full module breakdown, practical exercises, and tools.
            </p>
          </div>

          {/* 4 Courses Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CITY_COURSES.map((course, idx) => {
              const isCombo = course.id === "super-trader-combo" || course.id === "technical-analysis-options-combo-course" || (course.title && course.title.toLowerCase().includes("combo"));
              const isFree = course.id === "smart-trader-elementary" || course.fee === "FREE" || (course.fee && course.fee.includes("Free"));

              return (
                <div
                  key={course.id}
                  className={`relative flex flex-col justify-between rounded-3xl p-6 transition-all duration-300 border ${
                    isCombo
                      ? "bg-gradient-to-b from-[#06152B] to-[#040A14] border-[#00FF88]/40 shadow-xl shadow-[#00FF88]/10 hover:border-[#00FF88]"
                      : "bg-[#060D1A] border-white/[0.12] hover:border-white/[0.25] hover:bg-[#081224]"
                  }`}
                >
                  {/* Top Popular / Highlight Badge */}
                  {isCombo && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gradient-to-r from-[#00FF88] to-[#00F0FF] text-black font-extrabold text-[10px] uppercase tracking-wider shadow-md">
                      ★ Flagship Mentorship
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Header Tag & Level */}
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`text-[10px] font-mono px-2.5 py-1 rounded-full border font-bold ${
                          isFree
                            ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                            : isCombo
                            ? "bg-[#00FF88]/20 border-[#00FF88]/40 text-[#00FF88]"
                            : "bg-cyan-500/20 border-cyan-500/40 text-cyan-300"
                        }`}
                      >
                        {course.badge}
                      </span>
                      <span className="text-[11px] font-mono text-zinc-300 font-semibold">
                        {course.duration}
                      </span>
                    </div>

                    {/* Course Title & Subtitle */}
                    <div>
                      <h3 className="text-lg font-bold font-outfit text-white leading-snug">
                        {course.title}
                      </h3>
                      <p className="text-xs text-zinc-300 mt-1 line-clamp-2">
                        {course.subtitle}
                      </p>
                    </div>

                    {/* Pricing */}
                    <div className="pt-2 border-t border-white/[0.1]">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-2xl font-black font-outfit ${isCombo ? "text-[#00FF88]" : isFree ? "text-emerald-400" : "text-white"}`}>
                          {course.fee}
                        </span>
                        {course.originalFee && (
                          <span className="text-xs line-through text-zinc-400 font-semibold">
                            {course.originalFee}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] font-mono text-zinc-300 block mt-0.5 font-medium">
                        {course.sessions} • Live Market Training
                      </span>
                    </div>

                    {/* Core Highlights List */}
                    <div className="space-y-2 pt-2">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 font-semibold">
                        What you'll master:
                      </span>
                      <ul className="space-y-1.5">
                        {course.highlights.map((h, hIdx) => (
                          <li key={hIdx} className="flex items-start gap-2 text-xs text-zinc-200">
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#00FF88] shrink-0 mt-0.5" />
                            <span className="leading-tight">{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* CTAs */}
                  <div className="space-y-2.5 pt-6 mt-4 border-t border-white/[0.1]">
                    <button
                      type="button"
                      onClick={() => setModalCourse(course)}
                      className="w-full py-3 px-4 rounded-xl bg-[#0B1528] hover:bg-[#12223F] border border-cyan-500/35 hover:border-cyan-400 text-xs font-bold text-white transition-all flex items-center justify-center gap-2 shadow-xs group"
                    >
                      <BookOpen className="w-4 h-4 text-[#00F0FF] group-hover:scale-110 transition-transform shrink-0" />
                      <span className="text-white font-bold tracking-wide">View Full Syllabus ({course.modules?.length || 8} Modules)</span>
                    </button>

                    {isFree ? (
                      <button
                        type="button"
                        onClick={() => handleEnrollClick(course.title)}
                        className="w-full py-3 px-4 rounded-xl bg-[#00FF88] hover:bg-[#00e57a] text-black font-extrabold text-xs tracking-wider uppercase transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#00FF88]/25 hover:scale-[1.01] active:scale-[0.99]"
                      >
                        <span>Enroll for Free</span>
                        <ArrowRight className="w-4 h-4 text-black stroke-[2.5]" />
                      </button>
                    ) : isCombo ? (
                      <button
                        type="button"
                        onClick={() => handleEnrollClick(course.title)}
                        className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#00FF88] via-[#00F0FF] to-[#00FF88] text-black font-extrabold text-xs tracking-wider uppercase transition-all flex items-center justify-center gap-2 shadow-xl shadow-[#00FF88]/30 hover:opacity-95 hover:scale-[1.01] active:scale-[0.99]"
                      >
                        <span>Enroll in Flagship</span>
                        <ArrowRight className="w-4 h-4 text-black stroke-[2.5]" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleEnrollClick(course.title)}
                        className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs tracking-wider uppercase transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-950/60 border border-emerald-400/30 hover:scale-[1.01] active:scale-[0.99]"
                      >
                        <span>Enroll in Course</span>
                        <ArrowRight className="w-4 h-4 text-white stroke-[2.5]" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ═══ INTERACTIVE COURSE SYLLABUS MODAL (KHOLKE DEKH SAKE UNKE CONTENT KO) ═══ */}
      {modalCourse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
          <div
            className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-3xl bg-[#060D1A] border border-white/[0.15] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-white/[0.08] flex items-start justify-between gap-4 bg-white/[0.02]">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/30 text-[#00FF88] text-[11px] font-mono font-bold">
                    {modalCourse.badge}
                  </span>
                  <span className="text-xs font-mono text-zinc-400">
                    {modalCourse.sessions} • {modalCourse.duration}
                  </span>
                </div>
                <h3 className="text-2xl font-black font-outfit text-white">
                  {modalCourse.title}
                </h3>
                <p className="text-xs text-zinc-400">
                  {modalCourse.tagline}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setModalCourse(null)}
                className="p-2 rounded-xl bg-white/[0.05] border border-white/[0.1] text-zinc-400 hover:text-white hover:bg-white/[0.1] transition-colors shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div className="p-6 overflow-y-auto space-y-6 divide-y divide-white/[0.06]">
              {/* Course Outcome & Level */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] space-y-1">
                  <span className="text-[10px] font-mono uppercase text-zinc-400">Skill Level & Audience</span>
                  <p className="text-sm font-semibold text-white">{modalCourse.level}</p>
                </div>
                <div className="p-4 rounded-2xl bg-[#00FF88]/5 border border-[#00FF88]/20 space-y-1">
                  <span className="text-[10px] font-mono uppercase text-[#00FF88]">Course Fee & Access</span>
                  <p className="text-sm font-bold text-white">
                    {modalCourse.fee}{" "}
                    {modalCourse.originalFee && (
                      <span className="text-xs line-through text-zinc-500 font-normal ml-1">
                        {modalCourse.originalFee}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Learning Outcome */}
              <div className="pt-4 space-y-2">
                <h4 className="text-xs font-mono uppercase tracking-wider text-[#00F0FF] flex items-center gap-1.5">
                  <TargetIcon className="w-3.5 h-3.5" />
                  <span>Target Learning Outcome</span>
                </h4>
                <p className="text-sm text-zinc-300 leading-relaxed bg-white/[0.02] p-4 rounded-2xl border border-white/[0.05]">
                  {modalCourse.outcome}
                </p>
              </div>

              {/* Complete Modules List (Numbered) */}
              <div className="pt-4 space-y-3">
                <h4 className="text-xs font-mono uppercase tracking-wider text-white flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-[#00FF88]" />
                  <span>Complete Detailed Curriculum ({modalCourse.modules.length} Modules)</span>
                </h4>
                <div className="space-y-2.5">
                  {modalCourse.modules.map((mod, idx) => (
                    <div
                      key={idx}
                      className="p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-[#00FF88]/30 transition-colors flex items-start gap-3"
                    >
                      <span className="w-6 h-6 rounded-lg bg-[#00FF88]/10 text-[#00FF88] text-xs font-mono font-bold flex items-center justify-center shrink-0 mt-0.5">
                        {idx + 1}
                      </span>
                      <p className="text-xs sm:text-sm text-zinc-200 font-medium leading-relaxed">
                        {mod}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tools Covered */}
              <div className="pt-4 space-y-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
                  Trading Tools & Platforms Covered
                </span>
                <div className="flex flex-wrap gap-2 pt-1">
                  {modalCourse.toolsCovered.map((tool, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-xl bg-white/[0.04] border border-white/[0.08] text-xs font-mono text-zinc-300 flex items-center gap-1.5"
                    >
                      <Laptop className="w-3.5 h-3.5 text-[#00F0FF]" />
                      <span>{tool}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer CTAs */}
            <div className="p-4 sm:p-6 border-t border-white/[0.08] bg-black/60 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <span className="text-xs text-zinc-400">Ready to start?</span>
                <p className="text-sm font-bold text-white">Enroll in {modalCourse.title}</p>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setModalCourse(null)}
                  className="w-1/2 sm:w-auto px-5 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.1] text-xs font-semibold text-zinc-300 hover:text-white"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => handleEnrollClick(modalCourse.title)}
                  className="w-1/2 sm:w-auto px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#00FF88] to-[#00F0FF] text-black font-bold text-xs tracking-wide shadow-lg shadow-[#00FF88]/20 hover:opacity-95"
                >
                  Apply & Reserve Seat →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SECTION: MEET MENTOR DEEPAK SETHIA ═══ */}
      <section id="mentor" className="relative z-10 py-20 px-4 sm:px-6 lg:px-8 border-b border-white/[0.06] bg-[#02050A]">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-[#060D1A] via-[#040813] to-[#060D1A] border border-white/[0.1] p-8 sm:p-12 shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              
              {/* Real Mentor Photo */}
              <div className="lg:col-span-5 flex flex-col items-center text-center">
                <div className="relative w-64 h-64 sm:w-72 sm:h-72 rounded-3xl overflow-hidden border-2 border-[#00FF88]/30 shadow-2xl shadow-[#00FF88]/10 group">
                  <img
                    src="/images/cities/deepak_sir_real.png"
                    alt="Deepak Sethia - Lead Trading Mentor JamaDhan"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-left">
                    <span className="text-[10px] font-mono text-[#00FF88] bg-black/60 px-2 py-0.5 rounded-full border border-[#00FF88]/30 backdrop-blur-sm">
                      VERIFIED MENTOR
                    </span>
                  </div>
                </div>

                <div className="mt-4 space-y-1">
                  <h3 className="text-2xl font-black font-outfit text-white">
                    {MENTOR_INFO.name}
                  </h3>
                  <p className="text-xs font-mono text-[#00FF88]">
                    {MENTOR_INFO.role} • {MENTOR_INFO.experience}
                  </p>
                  <p className="text-xs text-zinc-400">
                    {MENTOR_INFO.credentials}
                  </p>
                </div>
              </div>

              {/* Mentor Bio & Philosophy */}
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/30 text-xs font-mono text-[#00FF88]">
                  <Award className="w-3.5 h-3.5" />
                  <span>Institutional Pedagogy</span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-black font-outfit text-white">
                  "We don't sell get-rich-quick fairy tales. We build disciplined institutional market operators."
                </h3>

                <p className="text-sm text-zinc-300 leading-relaxed">
                  {MENTOR_INFO.bio}
                </p>

                {/* Highlights Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  {MENTOR_INFO.highlights.map((h, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]"
                    >
                      <CheckCircle2 className="w-4 h-4 text-[#00FF88] shrink-0" />
                      <span className="text-xs text-zinc-200">{h}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-2 flex items-center gap-4">
                  <a
                    href="#enroll"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/[0.08] border border-white/[0.15] text-white font-semibold text-xs hover:bg-white/[0.15] transition-all"
                  >
                    <span>Book 1-on-1 Consultation Desk</span>
                    <ArrowRight className="w-3.5 h-3.5 text-[#00FF88]" />
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION: PHYSICAL TRADING FLOOR & ACADEMY INFRASTRUCTURE ═══ */}
      <section id="floor" className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 border-b border-white/[0.06] bg-[#030712]">
        <div className="max-w-6xl mx-auto space-y-10">
          
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/30 text-xs font-mono text-[#00FF88]">
              <Building2 className="w-3.5 h-3.5" />
              <span>Rajasthan's Premier Trading Infrastructure</span>
            </div>
            <h2 className="text-3xl font-black font-outfit text-white">
              Professional Live Trading Floor & Lounge
            </h2>
            <p className="text-xs sm:text-sm text-zinc-400">
              Students get access to real dual-screen Bloomberg/TradingView stations with gigabit fiber, trading live alongside Deepak Sir from 9:15 AM to 3:30 PM.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Real Trading Floor Image */}
            <div className="rounded-3xl overflow-hidden border border-white/[0.1] bg-[#060D1A] group">
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src="/trading_floor.png"
                  alt="Live Trading Floor Terminal Setup"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 space-y-1.5">
                <h4 className="text-base font-bold text-white">Live Market Floor & Terminal Desks</h4>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Equipped with 35+ professional dual-screen trading terminals, Bloomberg/TradingView Pro layouts, and high-speed execution.
                </p>
              </div>
            </div>

            {/* Real Reception / Center Lounge */}
            <div className="rounded-3xl overflow-hidden border border-white/[0.1] bg-[#060D1A] group">
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src="/jamadhan_reception.webp"
                  alt="JamaDhan Academy Reception and Lounge"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 space-y-1.5">
                <h4 className="text-base font-bold text-white">Academic Reception & Discussion Lounge</h4>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Consultation rooms, post-market debrief zones, and personal one-on-one trade journal review desks.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══ SECTION: BOOK FREE DEMO & ENROLLMENT FORM ═══ */}
      <section id="enroll" className="relative z-10 py-20 px-4 sm:px-6 lg:px-8 border-b border-white/[0.06] bg-[#02050A]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Contact Info & Value Prop */}
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/30 text-xs font-mono text-[#00FF88]">
                <Calendar className="w-3.5 h-3.5" />
                <span>Next Cohort Starts Monday</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-black font-outfit text-white tracking-tight">
                Enroll Today or Book a Free Live Floor Demo in {city.name}
              </h2>

              <p className="text-sm text-zinc-400 leading-relaxed">
                Experience our institutional teaching first-hand. Sit on our live trading floor, interact with Deepak Sir, and review our real trade journals before paying a single rupee.
              </p>

              {/* Address & Direct Phone Card */}
              <div className="p-5 rounded-2xl bg-[#060D1A] border border-white/[0.08] space-y-3">
                <div className="flex items-start gap-3 text-xs text-zinc-300">
                  <MapPin className="w-4 h-4 text-[#00FF88] shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-white block font-sans">Center Location:</strong>
                    <span>{city.address}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-zinc-300 pt-2 border-t border-white/[0.05]">
                  <Phone className="w-4 h-4 text-[#00F0FF] shrink-0" />
                  <div>
                    <span className="text-zinc-500 block">Direct Admissions Desk:</span>
                    <a href={`tel:${city.phone}`} className="text-white font-bold hover:text-[#00FF88] transition-colors">
                      {city.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-zinc-300 pt-2 border-t border-white/[0.05]">
                  <Mail className="w-4 h-4 text-[#00FF88] shrink-0" />
                  <div>
                    <span className="text-zinc-500 block">Official Support Email:</span>
                    <a href={`mailto:${city.email}`} className="text-white hover:text-[#00FF88] transition-colors">
                      {city.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Trust Callout */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.05] flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-[#00FF88] shrink-0" />
                <p className="text-xs text-zinc-400">
                  Zero pushy sales calls. You will be contacted directly by an academic mentor to answer your exact market learning goals.
                </p>
              </div>
            </div>

            {/* Right: The High-Converting Lead Form */}
            <div className="lg:col-span-7">
              <div className="rounded-3xl bg-[#060D1A] border border-white/[0.1] p-6 sm:p-10 shadow-2xl relative">
                
                {submitted ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[#00FF88]/20 border border-[#00FF88] text-[#00FF88] flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-black font-outfit text-white">
                      Inquiry Registered Successfully!
                    </h3>
                    <p className="text-sm text-zinc-300 max-w-md mx-auto">
                      Thank you, <strong className="text-white">{formData.name}</strong>. Our senior mentor will call you at <strong className="text-[#00FF88]">{formData.phone}</strong> to confirm your batch timings and invite you to the trading floor.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      className="px-6 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.1] text-xs font-semibold text-zinc-300 hover:text-white mt-4"
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div className="space-y-1 mb-2">
                      <h3 className="text-xl font-bold font-outfit text-white">
                        Book Free Demo / Reserve Seat
                      </h3>
                      <p className="text-xs text-zinc-400">
                        Fill in your details below. No upfront payment required.
                      </p>
                    </div>

                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-mono text-zinc-400 mb-1.5">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. Vikram Sharma"
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.1] text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#00FF88] transition-colors"
                      />
                    </div>

                    {/* Phone & Email Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-mono text-zinc-400 mb-1.5">
                          WhatsApp / Mobile No *
                        </label>
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="e.g. 98290XXXXX"
                          className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.1] text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#00FF88] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-mono text-zinc-400 mb-1.5">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="vikram@gmail.com"
                          className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.1] text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#00FF88] transition-colors"
                        />
                      </div>
                    </div>

                    {/* Program Selection */}
                    <div>
                      <label className="block text-xs font-mono text-zinc-400 mb-1.5">
                        Choose Your Program *
                      </label>
                      <select
                        value={formData.course}
                        onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[#050B16] border border-white/[0.1] text-white text-sm focus:outline-none focus:border-[#00FF88] transition-colors cursor-pointer"
                      >
                        {CITY_COURSES.map((c) => (
                          <option key={c.id} value={c.title} className="bg-[#060D1A] text-white">
                            {c.title} ({c.fee})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Learning Mode */}
                    <div>
                      <label className="block text-xs font-mono text-zinc-400 mb-1.5">
                        Preferred Learning Format
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, learningMode: "Offline Live Classroom" })}
                          className={`p-3 rounded-xl border text-xs font-semibold text-center transition-all ${
                            formData.learningMode === "Offline Live Classroom"
                              ? "bg-[#00FF88]/15 border-[#00FF88] text-white"
                              : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:text-white"
                          }`}
                        >
                          🏛 Offline Floor & Classroom
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, learningMode: "Live Online Interactive" })}
                          className={`p-3 rounded-xl border text-xs font-semibold text-center transition-all ${
                            formData.learningMode === "Live Online Interactive"
                              ? "bg-[#00F0FF]/15 border-[#00F0FF] text-white"
                              : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:text-white"
                          }`}
                        >
                          💻 Live Interactive Online
                        </button>
                      </div>
                    </div>

                    {/* Message / Questions */}
                    <div>
                      <label className="block text-xs font-mono text-zinc-400 mb-1.5">
                        Questions or Trading Experience (Optional)
                      </label>
                      <textarea
                        rows={2}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us about your background or questions for Deepak Sir..."
                        className="w-full px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.1] text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#00FF88] transition-colors"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 rounded-xl bg-gradient-to-r from-[#00FF88] via-[#00F0FF] to-[#00FF88] text-black font-extrabold text-sm tracking-wide shadow-xl shadow-[#00FF88]/20 hover:opacity-95 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 mt-2"
                    >
                      {submitting ? "Registering Your Seat..." : `Confirm Seat for ${city.name} Batch →`}
                    </button>
                  </form>
                )}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══ SECTION: STUDENT VIDEO TESTIMONIALS (4 IN A ROW, HOVER AUTOPLAY) ═══ */}
      <section id="reviews" className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 border-b border-white/[0.06] bg-[#030712]">
        <div className="max-w-7xl mx-auto space-y-10">
          
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/30 text-xs font-mono text-[#00FF88]">
              <Star className="w-3.5 h-3.5 text-[#00FF88] fill-[#00FF88]" />
              <span>Real Verified Alumni</span>
            </div>
            <h2 className="text-3xl font-black font-outfit text-white">
              Student Reviews & Transformations
            </h2>
            <p className="text-xs sm:text-sm text-zinc-400">
              Watch real testimonials from students across Rajasthan. Move your cursor over any video to play automatically.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VIDEO_TESTIMONIALS.map((review) => {
              const isPlaying = activeVideoId === review.id;

              return (
                <div
                  key={review.id}
                  className="rounded-3xl overflow-hidden bg-[#060D1A] border border-white/[0.08] hover:border-[#00FF88]/40 shadow-xl flex flex-col justify-between transition-all duration-300 group cursor-pointer"
                  onMouseEnter={() => {
                    const v = document.getElementById(`video-${review.id}`);
                    if (v) {
                      v.muted = isMuted;
                      v.play().catch(() => {});
                      setActiveVideoId(review.id);
                    }
                  }}
                  onMouseLeave={() => {
                    const v = document.getElementById(`video-${review.id}`);
                    if (v) {
                      v.pause();
                      setActiveVideoId((curr) => (curr === review.id ? null : curr));
                    }
                  }}
                >
                  <div className="relative aspect-[9/13] bg-black overflow-hidden">
                    <video
                      id={`video-${review.id}`}
                      src={review.videoSrc}
                      poster={review.thumbnail || ""}
                      playsInline
                      muted={isMuted}
                      loop
                      className="w-full h-full object-cover"
                      onPlay={() => setActiveVideoId(review.id)}
                      onPause={() => setActiveVideoId((curr) => (curr === review.id ? null : curr))}
                    />

                    {/* Video Overlay Badge when paused */}
                    {!isPlaying && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 group-hover:bg-black/10 transition-colors pointer-events-none">
                        <div className="w-12 h-12 rounded-full bg-[#00FF88] text-black flex items-center justify-center shadow-xl shadow-[#00FF88]/30 transform group-hover:scale-110 transition-transform">
                          <Play className="w-5 h-5 fill-black ml-0.5" />
                        </div>
                        <span className="text-[10px] font-mono text-white/90 bg-black/70 px-2 py-0.5 rounded-full mt-3 border border-white/10 backdrop-blur-sm">
                          Hover to play
                        </span>
                      </div>
                    )}

                    {/* Mute/Unmute toggle button when playing */}
                    {isPlaying && (
                      <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const nextMuted = !isMuted;
                            setIsMuted(nextMuted);
                            const v = document.getElementById(`video-${review.id}`);
                            if (v) v.muted = nextMuted;
                          }}
                          className="p-2 rounded-full bg-black/70 border border-white/20 text-white hover:text-[#00FF88] backdrop-blur-md transition-colors"
                          title={isMuted ? "Unmute sound" : "Mute sound"}
                        >
                          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-[#00FF88]" />}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="p-4 space-y-1.5 border-t border-white/[0.06] bg-[#060D1A]">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-white">{review.student}</h4>
                      <span className="text-[10px] font-mono text-[#00FF88] bg-[#00FF88]/10 px-2 py-0.5 rounded-full">
                        {review.city}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-400">
                      {review.tag} • <span className="text-zinc-300">{review.title}</span>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ═══ SECTION: POSITION SIZING & RISK MANAGEMENT SIMULATOR ═══ */}
      <section id="calculator" className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 border-b border-white/[0.06] bg-[#02050A]">
        <div className="max-w-5xl mx-auto space-y-10">
          
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-xs font-mono text-[#00F0FF]">
              <Calculator className="w-3.5 h-3.5" />
              <span>Capital Risk & Profit Simulator</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black font-outfit text-white tracking-tight">
              The Math of Sustainable Profitability
            </h2>
            <p className="text-xs sm:text-sm text-zinc-400">
              Calculate your exact risk, reward, and potential profit per trade with disciplined 1:2 risk-reward position sizing.
            </p>
          </div>

          <div className="rounded-3xl bg-[#070F1E] border border-white/[0.1] p-6 sm:p-10 shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-6 space-y-6">
                <div>
                  <div className="flex justify-between text-xs font-mono text-zinc-400 mb-2">
                    <span>Starting Trading Capital</span>
                    <span className="text-[#00FF88] font-bold text-sm font-sans">
                      ₹{capital.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="20000"
                    max="500000"
                    step="5000"
                    value={capital}
                    onChange={(e) => setCapital(Number(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00FF88]"
                  />
                  <div className="flex justify-between text-[10px] text-zinc-500 font-mono mt-1">
                    <span>₹20,000</span>
                    <span>₹2,50,000</span>
                    <span>₹5,00,000</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-mono text-zinc-400 mb-2">
                    <span>Strict Risk Per Trade (% of Capital)</span>
                    <span className="text-[#00F0FF] font-bold text-sm font-sans">
                      {riskPercent.toFixed(1)}% (₹{riskAmount.toLocaleString("en-IN")})
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="3.0"
                    step="0.1"
                    value={riskPercent}
                    onChange={(e) => setRiskPercent(Number(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00F0FF]"
                  />
                  <div className="flex justify-between text-[10px] text-zinc-500 font-mono mt-1">
                    <span>0.5% (Conservative)</span>
                    <span>1.5% (Optimal)</span>
                    <span>3.0% (Max)</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-mono text-zinc-400 mb-2">
                    <span>Target Risk:Reward Ratio</span>
                    <span className="text-white font-bold text-sm font-sans">
                      1 : {rrRatio.toFixed(1)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1.5"
                    max="3.5"
                    step="0.1"
                    value={rrRatio}
                    onChange={(e) => setRrRatio(Number(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white"
                  />
                  <div className="flex justify-between text-[10px] text-zinc-500 font-mono mt-1">
                    <span>1 : 1.5</span>
                    <span>1 : 2.0 (Standard)</span>
                    <span>1 : 3.5 (Trend Runner)</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-6 p-6 rounded-2xl bg-black/60 border border-white/[0.08] space-y-4">
                <span className="text-xs font-mono uppercase text-[#00FF88] tracking-wider">
                  20-Trade Expected Outcome (55% Win Rate)
                </span>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                    <span className="text-[10px] font-mono text-zinc-400">Target Profit per Win</span>
                    <p className="text-base font-bold text-[#00FF88] mt-0.5">
                      +₹{rewardAmount.toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                    <span className="text-[10px] font-mono text-zinc-400">Strict Max Stop Loss</span>
                    <p className="text-base font-bold text-rose-400 mt-0.5">
                      -₹{riskAmount.toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-gradient-to-r from-[#00FF88]/10 via-[#00F0FF]/10 to-transparent border border-[#00FF88]/30">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-300 font-medium">Net Profit on 20 Trades:</span>
                    <span className="text-xl font-black font-mono text-[#00FF88]">
                      +₹{simulatedNet.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-1">
                    Even with 9 losing trades, mathematical discipline generates consistent compounding!
                  </p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* ═══ SECTION: FREQUENTLY ASKED QUESTIONS ═══ */}
      <section id="faq" className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 border-b border-white/[0.06] bg-[#02050A]">
        <div className="max-w-4xl mx-auto space-y-8">
          
          <div className="text-center space-y-2">
            <h2 className="text-2xl sm:text-3xl font-black font-outfit text-white">
              Frequently Asked Questions in {city.name}
            </h2>
            <p className="text-xs sm:text-sm text-zinc-400">
              Clear answers regarding batch timings, floor residency, prerequisites, and fees.
            </p>
          </div>

          <div className="space-y-3">
            {currentCityFaqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              const rawQ = faq.question || faq.q || "";
              const rawA = faq.answer || faq.a || "";
              const question = rawQ.replace("{cityName}", city.name);
              const answer = rawA.replace("{cityName}", city.name);

              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-white/[0.08] bg-[#060D1A] overflow-hidden transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full p-4 sm:p-5 text-left flex items-center justify-between gap-4 font-semibold text-sm sm:text-base text-white hover:text-[#00FF88] transition-colors"
                  >
                    <span>{question}</span>
                    {isOpen ? (
                      <ChevronUp className="w-4 h-4 text-[#00FF88] shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-4 sm:px-5 pb-5 pt-1 text-xs sm:text-sm text-zinc-300 leading-relaxed border-t border-white/[0.04]">
                      {answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <Footer />

      {/* ═══ FLOATING WHATSAPP BUTTON (BOTTOM RIGHT) ═══ */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center">
        <a 
          href="https://wa.me/919257059361?text=Hi%20JamaDhan,%20I%20want%20to%20know%20more%20about%20your%20trading%20programs."
          target="_blank" 
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="w-13 h-13 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-110 active:scale-95 transition-all duration-300 border-2 border-white/20"
          style={{ width: "52px", height: "52px" }}
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
            <path d="M20.52 3.48A11.82 11.82 0 0 0 12.08 0C5.54 0 .22 5.32.22 11.86c0 2.09.55 4.13 1.59 5.93L.12 24l6.36-1.67a11.82 11.82 0 0 0 5.6 1.43h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.43-8.42ZM12.09 21.76h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.22-3.77.99 1.01-3.67-.24-.38a9.84 9.84 0 0 1-1.5-5.25C2.21 6.42 6.64 2 12.08 2c2.64 0 5.11 1.03 6.98 2.89a9.77 9.77 0 0 1 2.89 6.97c0 5.45-4.43 9.9-9.86 9.9Zm5.4-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.46-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.48.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
          </svg>
        </a>
      </div>
    </div>
  );
}

// Small helper icon component
function TargetIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}
